import { useEffect, useState } from "react";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import { useLanguage } from "@/i18n/LanguageContext";
import { projectConfig, explorerTokenUrl } from "@/config/project";
import { SELECTORS, ethCall, decodeString, formatUnits } from "@/lib/bsc";

export default function LiveInfo() {
  const { t } = useLanguage();
  const [chain, setChain] = useState({ loading: true, symbol: null, decimals: null, totalSupply: null, burned: null });

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      const [symbolHex, decimalsHex, supplyHex, deadHex] = await Promise.all([
        ethCall(SELECTORS.symbol, ctrl.signal),
        ethCall(SELECTORS.decimals, ctrl.signal),
        ethCall(SELECTORS.totalSupply, ctrl.signal),
        ethCall(SELECTORS.deadBalance, ctrl.signal),
      ]);
      if (ctrl.signal.aborted) return;
      const decimals = decimalsHex ? parseInt(decimalsHex, 16) : null;
      setChain({
        loading: false,
        symbol: symbolHex ? decodeString(symbolHex) : null,
        decimals: decimals != null && Number.isFinite(decimals) ? decimals : null,
        totalSupply: supplyHex && decimals != null ? formatUnits(supplyHex, decimals) : null,
        burned: deadHex && decimals != null ? formatUnits(deadHex, decimals) : null,
      });
    })();
    return () => ctrl.abort();
  }, []);

  const un = t("live.unavailable");
  const val = (v) => (chain.loading ? t("live.loading") : v ?? un);

  const metrics = [
    { key: "network", id: "metric-network-card", value: `${projectConfig.network} · ${projectConfig.tokenStandard}`, mono: false },
    { key: "contract", id: "metric-contract-card", value: `${projectConfig.contractAddress.slice(0, 8)}…${projectConfig.contractAddress.slice(-6)}`, mono: true, href: explorerTokenUrl, title: projectConfig.contractAddress },
    { key: "symbol", id: "metric-symbol-card", value: val(chain.symbol), mono: true },
    { key: "decimals", id: "metric-decimals-card", value: val(chain.decimals), mono: true },
    { key: "totalSupply", id: "metric-total-supply-card", value: val(chain.totalSupply), mono: true },
    { key: "burned", id: "metric-burned-card", value: val(chain.burned), mono: true },
    { key: "price", id: "metric-price-card", value: un, mono: false },
    { key: "marketCap", id: "metric-market-cap-card", value: un, mono: false },
  ];

  return (
    <section id="live-info" className="scroll-mt-24 bg-[#0D0608] px-4 py-24 sm:px-6 lg:px-8" data-testid="live-info-section">
      <div className="mx-auto max-w-7xl">
        <SectionHeader kicker={t("live.kicker")} title={t("live.title")} id="live-info" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {metrics.map((m, i) => {
            const inner = (
              <>
                <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{t(`live.metrics.${m.key}`)}</p>
                <p
                  className={`break-all text-sm font-semibold sm:text-base ${m.mono ? "font-mono" : ""} ${
                    m.value === un ? "text-zinc-600" : "text-zinc-100"
                  }`}
                  title={m.title}
                >
                  {m.value}
                </p>
              </>
            );
            return (
              <Reveal key={m.key} delay={0.04 * i}>
                {m.href ? (
                  <a
                    href={m.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={m.id}
                    className="block h-full rounded-xl border border-red-900/30 bg-[#140A0D] p-5 transition-colors hover:border-yellow-700/50"
                  >
                    {inner}
                  </a>
                ) : (
                  <div data-testid={m.id} className="h-full rounded-xl border border-red-900/30 bg-[#140A0D] p-5">
                    {inner}
                  </div>
                )}
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-3xl text-xs leading-relaxed text-zinc-500" data-testid="live-info-note">
            {t("live.note")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
